import { Injectable, effect, inject, signal } from '@angular/core';

import { ESHAVA_APP_CONFIG, readPreference, writePreference } from 'eshava-shared';

const STORAGE_SUFFIX = 'sidebar.collapsed';
const COMPACT_BREAKPOINT = 1440;

/**
 * Collapsed state of the sidebar.
 *
 * Toggling is manual, always. The size class only decides the **initial** state -- collapsed below
 * the normal class, where a 15rem sidebar is a noticeable share of the width. What must not happen
 * is a sidebar that opens and closes while the window is being resized: that reads as a defect, and
 * it moves the content under the pointer.
 */
@Injectable({ providedIn: 'root' })
export class SidebarStateService {
  private readonly storageKey = `${inject(ESHAVA_APP_CONFIG).preferencePrefix}.${STORAGE_SUFFIX}`;

  private readonly _isCollapsed = signal<boolean>(readInitialState(this.storageKey));

  readonly isCollapsed = this._isCollapsed.asReadonly();

  constructor() {
    effect(() => {
      writePreference(this.storageKey, String(this._isCollapsed()));
    });
  }

  /**
   * Opens the sidebar, if it is not already.
   *
   * Called when an area is clicked while collapsed: the labels are hidden there and the children go
   * with them, so a collapsed sidebar can reach the *first* screen of an area and none of the others.
   * Navigating and then finding no way on is worse than not navigating at all.
   */
  expand(): void {
    this._isCollapsed.set(false);
  }

  toggle(): void {
    this._isCollapsed.update(collapsed => !collapsed);
  }
}

function readInitialState(storageKey: string): boolean {
  const stored = readPreference(storageKey);
  if (stored === 'true' || stored === 'false') {
    return stored === 'true';
  }

  /* No stored choice: start collapsed on narrower screens. Read once, deliberately not observed. */
  return window.innerWidth < COMPACT_BREAKPOINT;
}